import React from 'react';
import { Lock } from 'lucide-react';
import { useUserContext } from '../contexts/UserContext';
import LoadingSpinner from './ui/LoadingSpinner';

interface RoleGuardProps {
    /** One or more roles that are allowed to view this content */
    allowedRoles: string | string[];
    children: React.ReactNode;
}

/**
 * Blocks rendering if the authenticated user does not have at least one of
 * the required roles.  Use this at the route level alongside FeatureGuard to
 * enforce role-based access in addition to subscription-feature checks.
 */
const RoleGuard: React.FC<RoleGuardProps> = ({ allowedRoles, children }) => {
    const { userContext, loading } = useUserContext();
    const roles = Array.isArray(allowedRoles) ? allowedRoles : [allowedRoles];

    if (loading) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center">
                <LoadingSpinner size="lg" />
            </div>
        );
    }

    const hasRole = userContext?.roles?.some(r => roles.includes(r.role)) ?? false;

    if (!hasRole) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] gap-5 px-4 text-center">
                <div className="w-16 h-16 rounded-2xl bg-red-50 flex items-center justify-center">
                    <Lock className="h-8 w-8 text-red-400" />
                </div>
                <div>
                    <h2 className="text-xl font-bold text-gray-800">Access Denied</h2>
                    <p className="text-sm text-gray-500 mt-2 max-w-sm">
                        You don't have permission to view this page.
                        Please contact your gym owner if you need access.
                    </p>
                </div>
            </div>
        );
    }

    return <>{children}</>;
};

export default RoleGuard;
